// Money formatting for the crash panel — an amount must NEVER be truncated,
// ellipsized, or clipped. When a formatted string is long, the FONT steps down
// (via `moneyLenClass` + the `.crash-money--len-*` rules in
// crash-chip-panel.scss); the digits always stay complete.

import { findConfig } from '@repo/money'
import { roundAmount } from 'src/utils/roundAmount'

/**
 * v15 (ASK 3 — cross-game precision parity): the crash panel's amount surfaces
 * (stake, riding value, cashed banner, busted loss) must show the SAME number
 * of decimals as every other chip-placement game's payout/balance pills, which
 * key off the currency's `displayPrecision` (the reference `getDisplayPrecision`
 * in src/utils/displayAmount.ts). Crash previously fed `formatMoney` the
 * currency's `inputPrecision` instead, which is the WIRE precision (4 for all
 * fiat, 6 for TRX) — so a EUR cashout could render "57.8156" / "0.3333" (3–4
 * decimals) where the rest of the app shows fiat at 2 decimals ("57.82",
 * "0.33"). Using `displayPrecision` fixes that: fiat/stablecoins → 2, BTC → 8,
 * TRX → 8, unknown → 2. formatMoney then trims trailing zeros for the crypto
 * (p > 2) path exactly as before, and the no-truncate font step-down is
 * untouched — this changes ONLY the decimal COUNT, never the magnitude.
 */
export function getCrashDisplayPrecision(currency: string): number {
  try {
    return findConfig(currency, currency).displayPrecision
  } catch {
    return 2
  }
}

/**
 * Format an amount at the given display precision:
 *
 *   - FLOOR (truncate toward zero) at `precision` decimals — the platform-wide
 *     money display convention (`roundAmount` backs displayMoney/displayAmount
 *     in every other game), so the win banner, the zone cards, the social feed
 *     rows, and the bet-slot notification all render the SAME string for the
 *     same backend payout: 1.806 p2 → "1.80" (never "1.81"). roundAmount's
 *     dust guard keeps the floor from eating a real cent (4.1 → "4.10")
 *   - group thousands with commas ("12,345.67")
 *   - fiat (precision <= 2): fixed decimals — 17.7 p2 → "17.70"
 *   - high-precision crypto (precision > 2): trailing zeros trimmed but at
 *     least 2 decimals kept — 0.00012345 p8 → "0.00012345", 1.5 p8 → "1.50"
 *   - non-finite input falls back to zero (never renders "NaN" as money)
 */
export function formatMoney(amount: number, precision: number): string {
  const p = Math.max(0, Math.min(8, Math.trunc(Number.isFinite(precision) ? precision : 2)))
  const safe = Number.isFinite(amount) ? amount : 0

  const magnitude = roundAmount(Math.abs(safe), p)

  let out = magnitude.toFixed(p)

  if (p > 2) {
    // Trim trailing zeros, but keep at least 2 decimals so crypto amounts
    // still read as money ("1.50", not "1.5").
    const [int = '0', frac = ''] = out.split('.')
    let trimmed = frac.replace(/0+$/, '')
    if (trimmed.length < 2) trimmed = frac.slice(0, 2)
    out = `${int}.${trimmed}`
  }

  const [intPart = '0', fracPart] = out.split('.')
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const formatted = fracPart !== undefined ? `${grouped}.${fracPart}` : grouped

  // Sign only when the floored magnitude is non-zero — never "-0.00".
  return safe < 0 && magnitude > 0 ? `-${formatted}` : formatted
}

/**
 * Class names for a formatted amount: the base `.crash-money` (tabular
 * numerals, nowrap) plus a `--len-N` step-down when the string is long, so the
 * font shrinks instead of the digits clipping.
 */
export function moneyLenClass(formatted: string): string {
  const len = formatted.length
  if (len >= 16) return 'crash-money crash-money--len-16'
  if (len >= 13) return 'crash-money crash-money--len-13'
  if (len >= 10) return 'crash-money crash-money--len-10'
  return 'crash-money'
}
